'use client';

import React, { useMemo, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';

import { Chip, ClickAwayListener, Fade, Paper } from '@mui/material';

import { AppIcon } from '@/components/AppIcon';
import {
    useMarkAllNotificationsAsRead,
    useMarkAsRead,
} from '@/hooks/apps/notification';
import { useInfiniteNotifications } from '@/hooks/apps/notification/useInfiniteNotifications';
import { useAppSelector } from '@/redux-store/hooks';
import { dateUtils } from '@/utils/date';
import { t } from '@/utils/lang';
import { limitCount } from '@/utils/limit-count';
import { notificationManager } from '@/utils/notification-navigator';
import type { NotificationItem } from './MenuListNotification';
import MenuListNotification from './MenuListNotification';
import NotificationButton from './NotificationButton';

const NotificationDropdown: React.FC = () => {
    const userId = useAppSelector(state => state.auth.user?.id);
    const router = useRouter();
    const [open, setOpen] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    // const [tabIndex, setTabIndex] = useState(0);
    const anchorRef = useRef<HTMLButtonElement>(null);

    // const isPermissionReminder = useMemo(
    //     () =>
    //         permissionManager.getActionPermissionTable(
    //             routeManager.REMINDER_SCHEDULE_NOTIFICATION
    //         ),
    //     []
    // ).detail;

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
        useInfiniteNotifications(userId);

    const markAllReadMutation = useMarkAllNotificationsAsRead(userId || '');
    const markAsReadMutation = useMarkAsRead(userId || '');

    // const markAsReadReminder = useMarkAllReminderNotificationsAsRead(
    //     userId || ''
    // );

    // const {
    //     data: reminderInf,
    //     fetchNextPage: fetchNextReminder,
    //     hasNextPage: hasNextReminder,
    //     isFetchingNextPage: fetchingNextReminder,
    //     isLoading: isLoadingReminder,
    // } = useInfiniteReminders(userId, isPermissionReminder);

    const notifications = (data?.pages ?? []).flatMap(
        p => p?.result?.items ?? []
    );

    // const reminderItemsRaw = (reminderInf?.pages ?? []).flatMap(
    //     p => p?.result?.items ?? []
    // );

    const unreadNotifications = useMemo(() => {
        if (!data?.pages?.length) return 0;

        return data.pages[0]?.result?.additionalInfo?.unreadNotifications ?? 0;
    }, [data]);

    const safeFetchNextNotification = React.useCallback(() => {
        if (!hasNextPage || isFetchingNextPage) return;
        fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // const safeFetchNextReminder = React.useCallback(() => {
    //     if (!hasNextReminder || fetchingNextReminder) return;
    //     fetchNextReminder();
    // }, [hasNextReminder, fetchingNextReminder, fetchNextReminder]);

    // const { reminderCount } = calculateUnreadCounts({
    //     notifications: [],
    //     reminders: reminderItemsRaw,
    //     isPermissionReminder,
    // });

    const handleClose = () => {
        setOpen(false);
        setTooltipOpen(false);
    };

    const handleToggle = () => {
        setOpen(prev => !prev);
    };

    // const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    //     setTabIndex(newValue);
    // };

    const notificationItems: NotificationItem[] = useMemo(
        () =>
            notifications.map(item => ({
                id: item.id,
                title: item.title,
                description: item.content,
                status: item.isRead,
                createdAt: dateUtils.getRelativeTime(item.createdDate),
                isRead: item.isRead,
                onClick: () => {
                    markAsReadMutation.mutate([item.id]);
                    handleClose();

                    const path = notificationManager.getPath(
                        item.notificationType,
                        item.relatedEntityId
                    );

                    router.push(path);
                },
            })),
        [notifications]
    );

    // const reminderItems: NotificationItem[] = useMemo(
    //     () =>
    //         reminderItemsRaw.map(item => ({
    //             id: item.id,
    //             title: `Nhắc lịch: ${item.name}`,
    //             description: item.content,
    //             status: item.isRead,
    //             createdAt: dateUtils.getRelativeTime(item.notificationDate),
    //             isRead: item.isRead,
    //             onClick: () => {
    //                 markAsReadReminder.mutate();
    //                 handleClose();
    //                 router.push(
    //                     routeManager
    //                         .getRouteMap()
    //                         [routeManager.DEVICE_DETAIL].path(item.deviceId)
    //                 );
    //             },
    //         })),
    //     [reminderItemsRaw]
    // );

    // const totalUnread = unreadNotifications + reminderCount;

    const totalUnread = unreadNotifications;

    return (
        <>
            <NotificationButton
                open={open}
                tooltipOpen={tooltipOpen}
                setTooltipOpen={setTooltipOpen}
                onToggle={handleToggle}
                ref={anchorRef}
                badgeCount={totalUnread}
            />

            <Popper
                open={open}
                transition
                disablePortal
                placement='bottom-start'
                anchorEl={anchorRef.current}
                className='min-is-[160px] !mbs-4 z-[1]'
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [0, 8] },
                    },
                ]}
            >
                {({ TransitionProps, placement }) => (
                    <Fade
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom-start'
                                    ? 'left top'
                                    : 'right top',
                        }}
                    >
                        <Paper className='shadow-lg' sx={{ width: 450 }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <Box>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            mx: 'auto',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                height: 48,
                                                display: 'flex',
                                                alignItems: 'center',
                                                fontWeight: 600,
                                                fontSize: 16,
                                                justifyContent: 'space-between',
                                                padding: '15px 20px',
                                            }}
                                        >
                                            {t('Thông báo')}
                                            {totalUnread > 0 && (
                                                <Chip
                                                    variant='tonal'
                                                    label={`${limitCount(totalUnread)} ${t('tin mới')}`}
                                                    color='error'
                                                    size='small'
                                                />
                                            )}
                                        </Box>

                                        {/* <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                justifyContent: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                overflow: 'visible',
                                            }}
                                        >
                                            {isPermissionReminder && (
                                                <NotificationTabs
                                                    value={tabIndex}
                                                    onChange={handleTabChange}
                                                    countNotify={
                                                        unreadNotifications
                                                    }
                                                    countReminder={
                                                        reminderCount
                                                    }
                                                />
                                            )}
                                        </Box> */}
                                    </Box>

                                    {/* {tabIndex === 0 || tabIndex === 1 ? ( */}
                                    <Box
                                        component='a'
                                        sx={{
                                            borderBottom: '1px solid #F3F3F3',
                                            width: '100%',
                                            height: '100%',
                                            justifyContent: 'right',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontSize: '14px',
                                            pr: 5,
                                            pb: 2.5,
                                            cursor:
                                                unreadNotifications <= 0
                                                    ? 'default'
                                                    : 'pointer',

                                            pointerEvents:
                                                unreadNotifications <= 0
                                                    ? 'none'
                                                    : 'auto',

                                            color:
                                                unreadNotifications <= 0
                                                    ? 'text.disabled'
                                                    : 'primary.main',

                                            // cursor:
                                            //     (tabIndex === 0 &&
                                            //         unreadNotifications <=
                                            //             0) ||
                                            //     (tabIndex === 1 &&
                                            //         reminderCount <= 0)
                                            //         ? 'default'
                                            //         : 'pointer',
                                            // pointerEvents:
                                            //     (tabIndex === 0 &&
                                            //         unreadNotifications <=
                                            //             0) ||
                                            //     (tabIndex === 1 &&
                                            //         reminderCount <= 0)
                                            //         ? 'none'
                                            //         : 'auto',
                                            // color:
                                            //     (tabIndex === 0 &&
                                            //         unreadNotifications <=
                                            //             0) ||
                                            //     (tabIndex === 1 &&
                                            //         reminderCount <= 0)
                                            //         ? 'text.disabled'
                                            //         : 'primary.main',
                                        }}
                                        onClick={() => {
                                            markAllReadMutation.mutate();

                                            // if (tabIndex === 0) {
                                            //     markAsReadMutation.mutate();
                                            // } else {
                                            //     markAsReadReminder.mutate();
                                            // }
                                        }}
                                    >
                                        <AppIcon
                                            name='doneAll'
                                            size={20}
                                            disabled={
                                                unreadNotifications <= 0

                                                // (tabIndex === 0 &&
                                                //     unreadNotifications <=
                                                //         0) ||
                                                // (tabIndex === 1 &&
                                                //     reminderCount <= 0)
                                            }
                                        />{' '}
                                        {t('Đánh dấu đọc tất cả')}
                                    </Box>
                                    {/* ) : null} */}

                                    <Box sx={{ width: '100%' }}>
                                        {/* {tabIndex === 0 && ( */}
                                        <>
                                            {isLoading ? (
                                                <Box
                                                    sx={{
                                                        p: 2,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {t('Đang tải...')}
                                                </Box>
                                            ) : (
                                                <MenuListNotification
                                                    items={notificationItems}
                                                    onEndReached={
                                                        safeFetchNextNotification
                                                    }
                                                    isFetchingNext={
                                                        isFetchingNextPage
                                                    }
                                                    hasNextPage={hasNextPage}
                                                />
                                            )}
                                        </>
                                        {/* )} */}
                                        {/* {tabIndex === 1 && (
                                            <>
                                                {isLoadingReminder ? (
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            textAlign: 'center',
                                                        }}
                                                    >
                                                        {t('Đang tải...')}
                                                    </Box>
                                                ) : (
                                                    <MenuListNotification
                                                        items={reminderItems}
                                                        onEndReached={
                                                            safeFetchNextReminder
                                                        }
                                                        isFetchingNext={
                                                            fetchingNextReminder
                                                        }
                                                        hasNextPage={
                                                            hasNextReminder
                                                        }
                                                    />
                                                )}
                                            </>
                                        )} */}
                                    </Box>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </>
    );
};

export default NotificationDropdown;
